import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { Observable, of } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ContactsPreferencesProperty } from "../models/properties.models";
import { ContactOverview } from "src/app/shared/models/contact-overview.model";

@Injectable({
  providedIn: "root",
})
export class ContactsService {
  constructor(
    private http: HttpClient,
    public _cookiesService: CookieService,
  ) {}

  /***
   * Get All costumers
   */
  getAllCostumers(page: number, search = "", contactsIds: any[] = []) {
    let params = new HttpParams().set("page", page);

    if (contactsIds.length > 0) {
      contactsIds.forEach((e) => {
        params = params.append("cs", e);
      });
    }
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/costumers`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapDataContacts(data)),
        catchError((error) => {
          return of({ costumers: [], total: 0 });
        }),
      );
  }

  /***
   * Get All costumers
   */
  getAllContacts(
    page: number,
    search = "",
    contactsIds: any[] = [],
    filter: "buyer" | "owner" | null = null,
    agentsIds: Array<string | number> = [],
    createdAtFrom: string | null = null,
    createdAtTo: string | null = null,
  ) {
    return this.requestAllContacts(
      page,
      search,
      contactsIds,
      filter,
      agentsIds,
      createdAtFrom,
      createdAtTo,
    ).pipe(
      catchError((error) => {
        return of(this.createEmptyContactsResponse());
      }),
    );
  }

  searchContacts(
    page: number,
    _pageSize: number,
    search = "",
    contactsIds: any[] = [],
    filter: "buyer" | "owner" | null = null,
    agentsIds: Array<string | number> = [],
    createdAtFrom: string | null = null,
    createdAtTo: string | null = null,
    storeIds: Array<string | number> = [],
    sourceIds: Array<string | number> = [],
  ) {
    return this.requestAllContacts(
      page,
      search,
      contactsIds,
      filter,
      agentsIds,
      createdAtFrom,
      createdAtTo,
      storeIds,
      sourceIds,
    ).pipe(
      catchError((error) => {
        return of(this.createEmptyContactsResponse());
      }),
    );
  }

  private requestAllContacts(
    page: number,
    search = "",
    contactsIds: any[] = [],
    filter: "buyer" | "owner" | null = null,
    agentsIds: Array<string | number> = [],
    createdAtFrom: string | null = null,
    createdAtTo: string | null = null,
    storeIds: Array<string | number> = [],
    sourceIds: Array<string | number> = [],
  ) {
    let params = new HttpParams().set("page", page);
    const sanitizedSearch = this.sanitizeSearchValue(search);

    if (contactsIds.length > 0) {
      contactsIds.forEach((e) => {
        params = params.append("cs", e);
      });
    }

    if (agentsIds.length > 0) {
      agentsIds.forEach((a) => {
        params = params.append("agentIds", `${a}`);
      });
    }

    (storeIds || []).forEach((s) => {
      params = params.append("storeIds", `${s}`);
    });

    (sourceIds || []).forEach((s) => {
      params = params.append("sourceIds", `${s}`);
    });

    if (sanitizedSearch !== "") params = params.set("search", sanitizedSearch);

    if (filter) params = params.set("type", filter);
    if (createdAtFrom) params = params.set("createdAtFrom", createdAtFrom);
    if (createdAtTo) params = params.set("createdAtTo", createdAtTo);

    return this.http
      .get<any>(`${environment.apiUrl}/contacts`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapContacts(data)),
      );
  }

  /**
   * Create contact costumers
   * @param data
   * @returns
   */
  createCostumers(
    data: any,
    option: { returnContact: boolean } = { returnContact: false },
  ) {
    let params = new HttpParams();
    if (option.returnContact) {
      params = params.append("rc", "c");
    }
    return this.http
      .post<any>(`${environment.apiUrl}/contacts/costumers`, data, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Histórico de observações do contacto. */
  getContactNotes(contactId: number) {
    return this.http
      .get<any[]>(`${environment.apiUrl}/contacts/costumer/${contactId}/notes`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Acrescenta uma observação; devolve o histórico atualizado. */
  addContactNote(contactId: number, body: string) {
    return this.http
      .post<any[]>(
        `${environment.apiUrl}/contacts/costumer/${contactId}/notes`,
        { body },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Update Costumers
   * @param data
   * @returns
   */
  /**
   * Características mais usadas nos imóveis da entidade — é o conjunto que se
   * mostra ao qualificar, em vez do catálogo inteiro.
   */
  getSuggestedCharacteristics() {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get<any>(
        `${environment.apiUrl}/contacts/characteristics/suggested/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /** Substitui o conjunto de características desejadas do contacto. */
  updateCostumerCharacteristics(id: any, characteristicIds: number[]) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/contacts/costumers/${id}/characteristics`,
        { characteristicIds },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Imóveis compatíveis com as preferências guardadas do contacto.
   * `includeMls` traz também os de outras entidades publicados em MLS — fora
   * por omissão, porque aqui o passo seguinte é associá-los ao negócio.
   */
  getSuggestedPropertiesForContact(
    contactId: any,
    includeMls = false,
    limit = 6,
  ) {
    const lang = this._cookiesService.get("lang");
    const params = new HttpParams()
      .set("includeMls", includeMls ? "true" : "false")
      .set("limit", `${limit}`);
    return this.http
      .get<any[]>(
        `${environment.apiUrl}/contacts/costumers/${contactId}/suggested-properties/${lang}`,
        { params, withCredentials: true },
      )
      .pipe(first());
  }

  /** Temperatura da lead: só o interestLevel de uma preferência (0-100). */
  updateCostumerInterestLevel(
    id: any,
    interestLevel: number,
    preferenceId?: number | null,
  ) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/contacts/costumers/${id}/interest-level`,
        { interestLevel, preferenceId: preferenceId ?? null },
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Atualização parcial e segura dos campos escalares do contacto. Não usar
   * updateCostumers para isto: esse endpoint apaga preferências e associações
   * de imóveis quando o payload não as inclui.
   */
  updateCostumerQuickInfo(id: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/contacts/costumers/${id}/quick-info`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Preferências do contacto (o que procura), lidas/gravadas à parte do resto
   * da ficha — ver `updateCostumerQuickInfo` para o porquê de não se usar o
   * PATCH completo.
   */
  /** Localização por defeito (loja → entidade) para qualificar sem preferências. */
  getDefaultPreferenceLocation() {
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/preferences/default-location`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Características desejadas já escolhidas, com nome traduzido. */
  getCostumerCharacteristics(id: any) {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get<any[]>(
        `${environment.apiUrl}/contacts/costumers/${id}/characteristics/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getCostumerPreferences(id: any) {
    return this.http
      .get<any[]>(`${environment.apiUrl}/contacts/costumers/${id}/preferences`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateCostumerPreferences(id: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/contacts/costumers/${id}/preferences`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }

  updateCostumers(id: any, data: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}/contacts/costumers/${id}`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /***
   * Get All owners
   */
  getAllOwners(page: number, search = "", contactsIds: any[] = []) {
    let params = new HttpParams().set("page", page);

    if (contactsIds.length > 0) {
      contactsIds.forEach((e) => {
        params = params.append("cs", e);
      });
    }
    if (search !== "") params = params.set("search", search);

    return this.http
      .get<any>(`${environment.apiUrl}/contacts/owners`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapDataContacts(data)),
        catchError((error) => {
          return of({ owners: [], total: 0 });
        }),
      );
  }

  /**
   * Create contact owners
   * @param data
   * @returns
   */
  createOwners(
    data: any,
    option: { returnContact: boolean } = { returnContact: false },
  ) {
    let params = new HttpParams();
    if (option.returnContact) {
      params = params.append("rc", "c");
    }
    return this.http
      .post<any>(`${environment.apiUrl}/contacts/owners`, data, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Update owners
   * @param data
   * @returns
   */
  updateOwners(id: any, data: any) {
    return this.http
      .patch<any>(`${environment.apiUrl}/contacts/owners/${id}`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /***
   * Get All contacts types
   */
  getContactsTypes() {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get<any>(`${environment.apiUrl}/contacts/contacts-types/${lang}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  /***
   * Get All contacts Data
   */
  getContactsData() {
    const lang = this._cookiesService.get("lang");

    return this.http
      .get<any>(`${environment.apiUrl}/contacts/contacts-data/${lang}`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  getContactSources() {
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/sources`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  createContactSource(data: { name: string }) {
    return this.http
      .post<any>(`${environment.apiUrl}/contacts/sources`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateContactSource(
    id: number,
    data: { name?: string; status?: boolean },
  ) {
    return this.http
      .patch<any>(`${environment.apiUrl}/contacts/sources/${id}`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteContactSource(id: number, moveToSourceId?: number | null) {
    return this.http
      .delete<any>(`${environment.apiUrl}/contacts/sources/${id}`, {
        body: { moveToSourceId },
        withCredentials: true,
      })
      .pipe(first());
  }

  mapDataContacts(data: any) {
    let contacts: any = {};

    contacts.total = data.total;

    if (data.costumers) {
      contacts.costumers =
        data.costumers.map((c: any) => {
          return {
            id: c.id,
            firstName: c.firstName,
            lastName: c.lastName,
            email: c.email,
            status: c.status,
            contactSource: c.contactSource,
            contactSourceDetails: c.contactSourceDetails,
            phoneNumber: c.phoneNumber,
            phone: c.phone,
            obs: c.obs,
            isSelected: false,
            isAddToProperty: false,
            isNew: false,
            file: c.file ? c.file : undefined,
            hasFile: c.hasFile,
            dataConsent: c.dataConsent,
            hasDeal: c.hasDeal,
            user: c.user,
            preferences: c?.preferences,
            propertyQuality: c?.propertyQuality,
          };
        }) || [];
    } else if (data.owners) {
      contacts.owners =
        data.owners.map((c: any) => {
          return {
            id: c.id,
            email: c.email,
            firstName: c.firstName,
            lastName: c.lastName,
            phone: c.phone,
            phoneNumber1: c.phoneNumber1,
            phoneNumber2: c.phoneNumber2,
            address: c.address,
            zipCode: c.zipCode,
            status: c.status,
            contactSource: c.contactSource,
            contactSourceDetails: c.contactSourceDetails,
            isSelected: false,
            isAddToProperty: false,
            isNew: false,
            preferences: c?.preferences,
            user: c.user,
            fiscalAddress: c?.fiscalAddress,
            fiscalCity: c?.fiscalCity,
            fiscalCountry: c?.fiscalCountry,
            fiscalPostalCode: c?.fiscalPostalCode,
            maritalStatus: c?.maritalStatus,
            propertyRegime: c?.propertyRegime,
            identificationDocument: c?.ownerIdentification,
            fiscalIdentification: c?.fiscalIdentification,
          };
        }) || [];
    }

    return contacts;
  }

  mapContacts(data: any) {
    let contacts: any = {};

    contacts.total = data.total;
    contacts.loginAgent = data.loginAgent;
    contacts.contacts =
      data.contacts.map((c: any) => {
        return {
          ...c,
          id: c.id,
          firstName: c.firstName,
          lastName: c.lastName,
          email: c.email,
          status: c.status,
          contactSource: c.contactSource,
          contactSourceDetails: c.contactSourceDetails,
          phoneNumber: c.phoneNumber,
          phoneNumber2: c.phoneNumber2,
          address: c?.address,
          zipCode: c?.zipCode,
          obs: c.obs,
          isSelected: false,
          isAddToProperty: false,
          isNew: false,
          file: c.file ? c.file : undefined,
          hasFile: c.hasFile,
          dataConsent: c.dataConsent,
          hasDeal: c.hasDeal,
          user: c.user,
          preferences: c?.preferences,
          propertyQuality: c?.propertyQuality,
          types: c?.types,
          additionalContacts: c?.additionalContacts,
          contactUser: c?.contactUser,
          loginAgent: c?.loginAgent,
        };
      }) || [];

    return contacts;
  }

  private createEmptyContactsResponse() {
    return { contacts: [], total: 0, loginAgent: false };
  }

  private sanitizeSearchValue(value: string) {
    return `${value ?? ""}`.trim().replace(/\s+/g, " ");
  }

  /***
   * Get Active Agents
   */
  getActiveAgents() {
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/agents`, {
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => this.mapActiveAgents(data)),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  private mapActiveAgents(data: any): any[] {
    const agents = this.extractActiveAgents(data);

    return agents
      .map((agent: any) => this.normalizeActiveAgent(agent))
      .filter(
        (agent: any) =>
          agent?.id !== null &&
          agent?.id !== undefined &&
          `${agent.id}`.trim().length > 0,
      );
  }

  private extractActiveAgents(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.agents)) {
      return data.agents;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.items)) {
      return data.items;
    }

    return [];
  }

  private normalizeActiveAgent(agent: any) {
    const id = agent?.id ?? agent?.userId ?? agent?.user?.id ?? null;
    const fullNameFromFields =
      `${agent?.firstName ?? ""} ${agent?.lastName ?? ""}`.trim();
    const userNameFromFields =
      `${agent?.user?.firstName ?? ""} ${agent?.user?.lastName ?? ""}`.trim();
    const name =
      `${agent?.name ?? ""}`.trim() ||
      `${agent?.fullName ?? ""}`.trim() ||
      fullNameFromFields ||
      `${agent?.user?.name ?? ""}`.trim() ||
      userNameFromFields ||
      `${agent?.email ?? ""}`.trim() ||
      `${agent?.user?.email ?? ""}`.trim();

    return {
      ...agent,
      id,
      name: name || `${id ?? ""}`,
    };
  }

  /**
   * Delete costumer
   * @param id
   * @returns
   */
  deleteCostumer(id: string) {
    return this.http
      .delete<any>(`${environment.apiUrl}/contacts/costumers/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Delete costumer
   * @param id
   * @returns
   */
  deleteOwner(id: string) {
    return this.http
      .delete<any>(`${environment.apiUrl}/contacts/owners/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /***
   * Get All properties
   */
  getAllProperties(page: number, search = "") {
    const lang = this._cookiesService.get("lang");
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/properties/${lang}`, {
        params,
        withCredentials: true,
      })
      .pipe(
        first(),
        map((data) => {
          return {
            properties:
              data.properties.map((p: any) => {
                return {
                  id: p.id,
                  title: p.title,
                  reference: p.reference,
                  propertyType: p.propertyType,
                  businessType: p.businessType,
                  price: p.price,
                  isSelected: false,
                  isVisible: true,
                  file: p.file
                    ? `${environment.apiUrl}${environment.dirPropertiesImages}/${p.file}`
                    : undefined,
                };
              }) || [],
            total: data.total,
          };
        }),
        catchError((error) => {
          return of({ properties: [], total: 0 });
        }),
      );
  }

  /***
   * Get All properties by contact
   */
  getAllPropertiesByContact(
    contact: string,
    contactId: number,
    page: number,
    search = "",
  ) {
    const lang = this._cookiesService.get("lang");

    let params = new HttpParams().set("page", page);

    if (search !== "") params = params.set("search", search);

    return this.http
      .get<any>(
        `${environment.apiUrl}/contacts/properties/${contact}/${contactId}/${lang}`,
        { params, withCredentials: true },
      )
      .pipe(
        first(),
        catchError((error) => {
          return of({ properties: [], total: 0 });
        }),
      );
  }

  /**
   * Delete all costumers by ids[]
   * @param ids
   * @returns
   */
  deleteAllCostumers(ids: any) {
    return this.http
      .delete<any>(`${environment.apiUrl}/contacts/allcostumers`, {
        withCredentials: true,
        body: { ids },
      })
      .pipe(first());
  }

  /**
   * Delete all owners by ids[]
   * @param ids
   * @returns
   */
  deleteAllOwners(ids: any) {
    return this.http
      .delete<any>(`${environment.apiUrl}/contacts/allowners`, {
        withCredentials: true,
        body: { ids },
      })
      .pipe(first());
  }

  /***
   * Get All costumers
   */
  getAllCostumersWithOutProperty(
    propertyId: any,
    page: number,
    search = "",
    ignoreContactsIds: any[],
    includeContactsIds: any[],
  ) {
    let params = new HttpParams().set("page", page);
    if (ignoreContactsIds.length > 0) {
      ignoreContactsIds.forEach((e) => {
        params = params.append("igcs", e);
      });
    }
    if (includeContactsIds.length > 0) {
      includeContactsIds.forEach((e) => {
        params = params.append("cs", e);
      });
    }
    if (search !== "") params = params.set("search", search);
    if (propertyId) {
      return this.http
        .get<any>(
          `${environment.apiUrl}/contacts/contact-without-property/property/${propertyId}/costumers`,
          { params, withCredentials: true },
        )
        .pipe(
          first(),
          map((data) => this.mapDataContacts(data)),
          catchError((error) => {
            return of({ costumers: [], total: 0 });
          }),
        );
    }
    return this.getAllCostumers(page, search, ignoreContactsIds);
  }

  /***
   * Get All owners
   */
  getAllOwnersWithOutProperty(
    propertyId: any,
    page: number,
    search = "",
    ignoreContactsIds: any[],
    includeContactsIds: any[],
  ) {
    let params = new HttpParams().set("page", page);
    if (ignoreContactsIds.length > 0) {
      ignoreContactsIds.forEach((e) => {
        params = params.append("igcs", e);
      });
    }
    if (includeContactsIds.length > 0) {
      includeContactsIds.forEach((e) => {
        params = params.append("cs", e);
      });
    }
    if (search !== "") params = params.set("search", search);
    if (propertyId) {
      return this.http
        .get<any>(
          `${environment.apiUrl}/contacts/contact-without-property/property/${propertyId}/owners`,
          { params, withCredentials: true },
        )
        .pipe(
          first(),
          map((data) => this.mapDataContacts(data)),
          catchError((error) => {
            return of({ costumers: [], total: 0 });
          }),
        );
    }
    return this.getAllOwners(page, search, ignoreContactsIds);
  }

  /***
   * Get All costumers
   */
  getAllCostumersHasProperty(
    propertyId: any,
    page: number,
    search = "",
    includeContactsIds: any[] = [],
    ignoreContactsIds: any[] = [],
  ) {
    if (propertyId) {
      let params = new HttpParams().set("page", page);
      if (includeContactsIds.length > 0) {
        includeContactsIds.forEach((e) => {
          params = params.append("cs", e);
        });
      }
      if (ignoreContactsIds.length > 0) {
        ignoreContactsIds.forEach((e) => {
          params = params.append("igcs", e);
        });
      }
      if (search !== "") params = params.set("search", search);
      return this.http
        .get<any>(
          `${environment.apiUrl}/contacts/contact-has-property/property/${propertyId}/costumers`,
          { params, withCredentials: true },
        )
        .pipe(
          first(),
          map((data) => this.mapDataContacts(data)),
          catchError((error) => {
            return of({ costumers: [], total: 0 });
          }),
        );
    }
    return this.getSomeCostumers(includeContactsIds, page, search);
  }

  /***
   * Get All costumers
   */
  getAllOwnersHasProperty(
    propertyId: any,
    page: number,
    search = "",
    includeContactsIds: any[] = [],
    ignoreContactsIds: any[] = [],
  ) {
    if (propertyId) {
      let params = new HttpParams().set("page", page);
      if (includeContactsIds.length > 0) {
        includeContactsIds.forEach((e) => {
          params = params.append("cs", e);
        });
      }
      if (ignoreContactsIds.length > 0) {
        ignoreContactsIds.forEach((e) => {
          params = params.append("igcs", e);
        });
      }
      if (search !== "") params = params.set("search", search);
      return this.http
        .get<any>(
          `${environment.apiUrl}/contacts/contact-has-property/property/${propertyId}/owners`,
          { params, withCredentials: true },
        )
        .pipe(
          first(),
          map((data) => this.mapDataContacts(data)),
          catchError((error) => {
            return of({ costumers: [], total: 0 });
          }),
        );
    }
    return this.getSomeOwners(includeContactsIds, page, search);
  }

  /***
   * Get some costumers
   */
  getSomeCostumers(
    contactsIds: any[] = [],
    page: number,
    search = "",
    ignoreContactsIds: any[] = [],
  ) {
    let params = new HttpParams().set("page", page);
    if (contactsIds.length > 0) {
      contactsIds.forEach((e) => {
        params = params.append("cs", e);
      });

      if (ignoreContactsIds.length > 0) {
        ignoreContactsIds.forEach((e) => {
          params = params.append("igcs", e);
        });
      }
      if (search !== "") params = params.set("search", search);

      return this.http
        .get<any>(`${environment.apiUrl}/contacts/some-contacts/costumers`, {
          params,
          withCredentials: true,
        })
        .pipe(
          first(),
          map((data) => this.mapDataContacts(data)),
          catchError((error) => {
            return of({ costumers: [], total: 0 });
          }),
        );
    }
    return of({ costumers: [], total: 0 });
  }

  /***
   * Get some owners
   */
  getSomeOwners(
    contactsIds: any[] = [],
    page: number,
    search = "",
    ignoreContactsIds: any[] = [],
  ) {
    let params = new HttpParams().set("page", page);
    if (contactsIds.length > 0) {
      contactsIds.forEach((e) => {
        params = params.append("cs", e);
      });

      if (ignoreContactsIds.length > 0) {
        ignoreContactsIds.forEach((e) => {
          params = params.append("igcs", e);
        });
      }
      if (search !== "") params = params.set("search", search);

      return this.http
        .get<any>(`${environment.apiUrl}/contacts/some-contacts/owners`, {
          params,
          withCredentials: true,
        })
        .pipe(
          first(),
          map((data) => this.mapDataContacts(data)),
          catchError((error) => {
            return of({ costumers: [], total: 0 });
          }),
        );
    }
    return of({ owners: [], total: 0 });
  }

  getCountries(): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/countries/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getRegions(cid: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/regions/${cid}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getDistricts(rid: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/districts/${rid}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getCounties(did: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/counties/${did}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getParish(cid: any): Observable<any> {
    const lang = this._cookiesService.get("lang");
    return this.http
      .get(
        `${environment.apiUrl}/get-form-properties/address/parishes/${cid}/${lang}`,
        { withCredentials: true },
      )
      .pipe(first());
  }

  getPropertiesByPreferences(page: any, data: any, search: string) {
    const lang = this._cookiesService.get("lang");
    let params = new HttpParams().set("page", page);
    if (search && search !== "") {
      params = params.set("search", search);
    }
    return this.http
      .post<{
        properties: ContactsPreferencesProperty[];
        total: number;
      }>(
        `${environment.apiUrl}/contacts/properties-by-preferences/${lang}`,
        data,
        { params, withCredentials: true },
      )
      .pipe(first());
  }

  getPropertiesWithoutPreferences(
    page: any,
    search: any = "",
    scope: "mine" | "store" | null = null,
    contactRule: boolean = false,
  ) {
    const lang = this._cookiesService.get("lang");
    let params = new HttpParams().set("page", page);
    if (search !== "") params = params.set("search", search);
    if (scope) params = params.set("scope", scope);
    if (contactRule) params = params.set("contactRule", "1");
    return this.http
      .get<{
        properties: ContactsPreferencesProperty[];
        total: number;
      }>(
        `${environment.apiUrl}/contacts/properties-without-preferences/${lang}`,
        { params, withCredentials: true },
      )
      .pipe(first());
  }

  filterPropertiesPref(
    properties: ContactsPreferencesProperty[],
    total: any,
    search: string,
  ) {
    if (search) {
      search = search.toLowerCase();
      const prop = properties.filter((p) => {
        return (
          p.reference?.toLowerCase().includes(search) ||
          p.title?.toLowerCase().includes(search) ||
          p.district?.includes(search) ||
          p.county?.includes(search)
        );
      });
      return of({ properties: prop, total: prop.length });
    }
    return of({ properties: properties, total: total });
  }

  findContactsWithPreferencesByProperty(propertyId: any) {
    const lang = this._cookiesService.get("lang");
    let params = new HttpParams().set("lang", lang);
    return this.http.get<any>(
      `${environment.apiUrl}/contacts/contacts-with-preferences/properties/${propertyId}`,
      { withCredentials: true, params },
    );
  }

  addContactToProperty(propertyId: any, contactId: any) {
    const data = {
      propertyId: propertyId,
      contactId: contactId,
      add: true,
    };
    return this.http
      .post<any>(`${environment.apiUrl}/contacts/contact-to-property`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  removeContactFromProperty(propertyId: any, contactId: any) {
    const data = {
      propertyId: propertyId,
      contactId: contactId,
      add: false,
    };
    return this.http
      .post<any>(`${environment.apiUrl}/contacts/contact-to-property`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Como `contactExists`, mas devolve o contacto encontrado — para o ecrã
   * sugerir "usar este contacto" em vez de criar um duplicado.
   */
  findExistingContact(value: any): Observable<{ exists: boolean; contact: any | null }> {
    return this.http
      .post<{ exists: boolean; contact: any | null }>(
        `${environment.apiUrl}/contacts/exist-contact/details`,
        { value },
        { withCredentials: true },
      )
      .pipe(first());
  }

  contactExists(value: any): Observable<boolean> {
    const data = {
      value: value,
    };

    return this.http
      .post<boolean>(`${environment.apiUrl}/contacts/exist-contact`, data, {
        withCredentials: true,
      })
      .pipe(
        first(),
        catchError((error) => {
          console.error("Erro na verificação", error);
          return of(false);
        }),
      );
  }

  getContactHistory(contactId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${environment.apiUrl}/contacts/${contactId}/history`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /** Visão 360º do contacto (leads, negócios, imóveis, contadores). */
  getContactOverview(
    contactId: number,
    lang: string | null,
  ): Observable<ContactOverview> {
    const params = lang ? new HttpParams().set("lang", lang) : undefined;
    return this.http
      .get<ContactOverview>(
        `${environment.apiUrl}/contacts/${contactId}/overview`,
        { withCredentials: true, params },
      )
      .pipe(first());
  }

  createAdditionalContact(id: number, value: any[]) {
    const data = {
      value: value,
      id: id,
    };

    return this.http
      .post<any>(
        `${environment.apiUrl}/contacts/create-additional-contact`,
        data,
        {
          withCredentials: true,
        },
      )
      .pipe(
        first(),
        catchError((error) => {
          console.error("Erro na verificação", error);
          return of(false);
        }),
      );
  }

  /**
   * Update Additional Contact
   * @param data
   * @returns
   */
  updateAdditionalContact(id: any, data: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/contacts/update-additional/${id}`,
        data,
        {
          withCredentials: true,
        },
      )
      .pipe(first());
  }

  /**
   * Update Additional Contact
   *
   * @returns
   */
  updateDefaultAdditionalContact(id: any) {
    return this.http
      .patch<any>(
        `${environment.apiUrl}/contacts/update-default-additional-contact/${id}`,
        {}, // corpo vazio
        { withCredentials: true },
      )
      .pipe(first());
  }

  /**
   * Delete costumer
   * @param id
   * @returns
   */
  deleteAdditionalContact(id: string) {
    return this.http
      .delete<any>(`${environment.apiUrl}/contacts/delete-additional/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  findContact(id: number) {
    return this.http
      .get<any>(`${environment.apiUrl}/contacts/find-contact/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  /**
   * Delete agent
  //  * @param id
  //  * @returns
  //  */
  // deleteAgent(data: any) {
  //   console.log('id service front', data)
  //   return this.http
  //     .delete<any>(`${environment.apiUrl}/contacts/delete-agent`, {
  //       body: data,
  //       withCredentials: true,
  //     })
  //     .pipe(first());
  // }
}
